// API service for Swipey.ai authentication endpoints
import { getEfTransactionId } from './everflow'
import { getTrackingParams } from './tracking'
import { setStorage, getStorage, setAuth, isAnnualPlan } from './storage'
import { LP_SESSION_TTL_MS } from './session'
import { resolvePaywallPath, isPopupFirst, isDuelQuiz } from './paywall'
import { applyProtection } from './variation'
import { getGbDeviceId, getGbCountryCode } from '../lib/growthbookClient'

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL

const API_BASE_URL = `${SWIPEY_BASE_URL}/api/v1`

const API_KEY = process.env.REACT_APP_API_KEY

// Lander identifier sent to the backend so registrations can be attributed to
// this LP, regardless of whether the user signs up via email or Google.
const LP_SOURCE = process.env.REACT_APP_VERCEL_ENV ? 'createsexfriend' : 'swipey.ai/createsexfriend';
// Voice label mapping (UI value → API label)
const voiceLabelMap = {
  'Voice 3': 'Teen (18+)',
  'Voice 5': 'Mature',
  // Voice 4 (Soft) doesn't exist in API → returns null intentionally
}

// Ethnicity mapping
const ethnicityLabelMap = {
  Arab: 'Mid.Eastern',
}

// Relationship mapping
const relationshipLabelMap = {
  stepSis: 'Step Sister',
  stepMom: 'Step Mom',
  girlfriend: 'Girlfriend',
  sexFriend: 'Fuck Buddy',
}

// New personality values (createsexfriend-isPersonality flag) → backend
// Personality-attribute labels. NOTE: our UI "Playful" maps to backend "Jester".
const personalityLabelMap = {
  playful: 'Jester',
  nympho: 'Nympho',
  submissive: 'Submissive',
  dominant: 'Dominant',
}
// Values that must be routed to the `personality` attribute (array of ids),
// NOT to `relationship` (legacy stepSis/girlfriend/... still go to relationship).
const PERSONALITY_VALUES = new Set(Object.keys(personalityLabelMap))

// Attributes map cache
let attributesMap = {}
let attributesLoaded = false

// Common headers for all API requests
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'x-api-key': API_KEY,
})

// External-click-id keys cache. The list of keys (e.g. ['twclid','gclid','fbclid','ttclid'])
// is owned by the backend; we fetch it once per page load and reuse it for every auth call.
let externalClickIdKeys = null
let externalClickIdKeysPromise = null

// Snapshot the landing-URL query params into sessionStorage on module load.
// We read externalClickIds from this snapshot — NOT from localStorage `utm_data`
// (which merges across visits and leaks stale click-ids), and NOT from
// `window.location.search` at submit time (SPA navigation strips it).
// sessionStorage clears when the tab closes → each tab starts fresh.
const LANDING_URL_PARAMS_KEY = 'landing_url_params'
;(() => {
  if (typeof window === 'undefined') return
  try {
    if (sessionStorage.getItem(LANDING_URL_PARAMS_KEY)) return
    const snapshot = {}
    new URLSearchParams(window.location.search).forEach((value, key) => {
      snapshot[key] = value
    })
    sessionStorage.setItem(LANDING_URL_PARAMS_KEY, JSON.stringify(snapshot))
  } catch {}
})()

// Fetch the list of click-id keys the backend wants us to forward. Cached for the page lifetime;
// in-flight requests are deduped via `externalClickIdKeysPromise`. Failures / timeouts fall back
// to [] so auth + OAuth redirects can't be blocked by this endpoint — externalClickIds gets sent as {}.
const EXTERNAL_CLICK_ID_KEYS_TIMEOUT_MS = 3000

const fetchExternalClickIdKeys = async () => {
  if (Array.isArray(externalClickIdKeys)) return externalClickIdKeys
  if (externalClickIdKeysPromise) return externalClickIdKeysPromise

  externalClickIdKeysPromise = (async () => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), EXTERNAL_CLICK_ID_KEYS_TIMEOUT_MS)
      : null
    try {
      const response = await fetch(`${API_BASE_URL}/marketing/external-click-id-keys`, {
        method: 'GET',
        headers: getHeaders(),
        signal: controller?.signal,
      })
      if (!response.ok) {
        externalClickIdKeys = []
        return externalClickIdKeys
      }
      const data = await response.json()
      externalClickIdKeys = Array.isArray(data) ? data : []
      return externalClickIdKeys
    } catch (err) {
      console.warn('[externalClickIds] keys fetch failed, falling back to []', err)
      externalClickIdKeys = []
      return externalClickIdKeys
    } finally {
      if (timeoutId) clearTimeout(timeoutId)
      externalClickIdKeysPromise = null
    }
  })()

  return externalClickIdKeysPromise
}

// Build the externalClickIds object the backend expects: for each key returned by
// /marketing/external-click-id-keys, pull the value from this tab's landing-URL snapshot.
// Source rationale (see LANDING_URL_PARAMS_KEY comment above):
//   - NOT localStorage.utm_data → leaks click-ids from previous visits
//   - NOT window.location.search at submit time → SPA navigation strips it
export const getExternalClickIds = async () => {
  const keys = await fetchExternalClickIdKeys()
  let landingParams = {}
  try {
    const raw = sessionStorage.getItem(LANDING_URL_PARAMS_KEY)
    if (raw) landingParams = JSON.parse(raw) || {}
  } catch {
    landingParams = {}
  }

  return keys.reduce((acc, key) => {
    const value = landingParams[key]
    acc[key] = value == null || value === '' ? null : value
    return acc
  }, {})
}

// Generic API request function
const apiRequest = async (endpoint, method = 'GET', body = null) => {
  try {
    const config = {
      method,
      headers: getHeaders(),
    }

    if (body) {
      config.body = JSON.stringify(body)
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, config)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`)
    }

    return data
  } catch (error) {
    throw error
  }
}

// Load attributes from API
export const loadCharacterAttributes = async () => {
  if (attributesLoaded) return attributesMap

  try {
    const response = await fetch(`${API_BASE_URL}/characters-attributes`, {
      method: 'GET',
      headers: getHeaders(),
    })

    if (!response.ok) {
      throw new Error('❌ Unable to load attributes')
    }

    const result = await response.json()

    result.forEach(attr => {
      if (!attr.type || !attr.label || !attr._id) return
      const type = attr.type.toLowerCase()
      if (!attributesMap[type]) attributesMap[type] = {}

      if (attr.style && attr.gender) {
        const key = `${attr.label.trim()}_${attr.style}_${attr.gender}`
        attributesMap[type][key] = attr._id
        // Voice: style is irrelevant for our UI — also store under plain label
        if (type === 'voice') {
          attributesMap[type][attr.label.trim()] = attr._id
        }
      } else {
        attributesMap[type][attr.label.trim()] = attr._id
      }
    })

    console.log('✅ Attributes loaded. Ethnicity keys:', Object.keys(attributesMap.ethnicity || {}))
    console.log('✅ Relationship keys:', Object.keys(attributesMap.relationship || {}))
    console.log('✅ Personality keys:', Object.keys(attributesMap.personality || {}))
    console.log('✅ Voice keys:', Object.keys(attributesMap.voice || {}))
    console.log('✅ Age keys:', Object.keys(attributesMap.age || {}))

    attributesLoaded = true
    return attributesMap
  } catch (err) {
    return {}
  }
}

// Helper function to get attribute ID with style and gender matching
export const getAttributeId = (type, label, userStyle, userGender) => {
  if (!attributesMap[type] || !label) return null

  // Handle voice mapping
  if (type === 'voice' && voiceLabelMap[label]) {
    label = voiceLabelMap[label]
  }

  // Handle ethnicity mapping
  if (type === 'ethnicity' && ethnicityLabelMap[label]) {
    label = ethnicityLabelMap[label]
  }

  // Handle relationship mapping
  if (type === 'relationship' && relationshipLabelMap[label]) {
    label = relationshipLabelMap[label]
  }

  // Handle personality mapping (UI value → backend label, e.g. playful→Jester)
  if (type === 'personality' && personalityLabelMap[label]) {
    label = personalityLabelMap[label]
  }

  // First try to find with exact style and gender match
  if (userStyle && userGender) {
    const styleKey = `${label.trim()}_${userStyle}_${userGender}`
    if (attributesMap[type][styleKey]) {
      return attributesMap[type][styleKey]
    }
  }

  // Fallback to plain label
  return attributesMap[type][label.trim()] || null
}

const apiToCamelCaseMap = {
  ethnicity: 'ethnicity',
  age: 'ageAttribute',
  eyecolor: 'eyeColor',
  hairstyle: 'hairStyle',
  haircolor: 'hairColor',
  bodytype: 'bodyType',
  breastsize: 'breastSize',
  buttsize: 'buttSize',
  personality: 'personality',
  occupation: 'occupation',
  hobbies: 'hobbies',
  relationship: 'relationship',
  voice: 'defaultVoice',
}

// Convert quiz selections to API format
export const convertSelectionsToApiFormat = async selections => {
  // Ensure attributes are loaded
  await loadCharacterAttributes()

  const userAnswers = {
    gender: 'Girls',
    style: selections?.welcome === 'Anime' ? 'Anime' : 'Realistic',
    attributes: {},
  }

  const { style, gender } = userAnswers
  console.log('🎨 convertSelectionsToApiFormat — style:', style, 'gender:', gender)

  // Step1: ethnicity
  if (selections?.step1) {
    if (selections.step1.ethnicity) {
      const id = getAttributeId('ethnicity', selections.step1.ethnicity, style, gender)
      console.log(`🔍 ethnicity lookup: "${selections.step1.ethnicity}" + style=${style} + gender=${gender} → id=${id}`)
      if (id) userAnswers.attributes[apiToCamelCaseMap.ethnicity] = id
    }
  }

  // Age lives in a different step per design: the `/ndg` design renders the slider in
  // Step1, the classic design in Step4. Step1 ALWAYS writes an age (its default, 22) —
  // `validateStepData('step1')` requires the field — so `step1.age` is a placeholder in
  // the classic flow and would mask the value the user actually picked. Prefer `step4.age`
  // exactly like the Step5 summary card does, so model-params matches what was shown.
  const selectedAge = selections?.step4?.age || selections?.step1?.age
  if (selectedAge) {
    userAnswers.attributes[apiToCamelCaseMap.age] = selectedAge
  }

  // Step2: body attributes
  if (selections?.step2) {
    const step2Map = {
      bodyType: 'bodytype',
      breastSize: 'breastsize',
      buttSize: 'buttsize',
    }
    Object.entries(step2Map).forEach(([key, apiKey]) => {
      if (selections.step2[key]) {
        const id = getAttributeId(apiKey, selections.step2[key], style, gender)
        if (id) userAnswers.attributes[apiToCamelCaseMap[apiKey]] = id
      }
    })
  }

  // Step3: hair, eyes
  if (selections?.step3) {
    const step3Map = {
      hairColor: 'haircolor',
      hairStyle: 'hairstyle',
      eyeColor: 'eyecolor',
    }
    Object.entries(step3Map).forEach(([key, apiKey]) => {
      if (selections.step3[key]) {
        const id = getAttributeId(apiKey, selections.step3[key], style, gender)
        if (id) userAnswers.attributes[apiToCamelCaseMap[apiKey]] = id
      }
    })
  }

  // Step4: personality, voice
  if (selections?.step4) {
    if (selections.step4.personality) {
      const rawPersonality = Array.isArray(selections.step4.personality)
        ? selections.step4.personality
        : [selections.step4.personality]

      // New personalities (Playful/Nympho/Submissive/Dominant) → backend
      // `personality` attribute (array of ids). Legacy values
      // (stepSis/girlfriend/...) → `relationship` (single id).
      const personalityValues = rawPersonality.filter(p => PERSONALITY_VALUES.has(p))
      const relationshipValues = rawPersonality.filter(p => !PERSONALITY_VALUES.has(p))

      if (personalityValues.length > 0) {
        const personalityIds = personalityValues
          .map(p => getAttributeId('personality', p, style, gender))
          .filter(Boolean)
        if (personalityIds.length > 0) {
          userAnswers.attributes[apiToCamelCaseMap.personality] = personalityIds
        }
      }

      if (relationshipValues.length > 0) {
        const relationshipIds = relationshipValues
          .map(p => getAttributeId('relationship', p, style, gender))
          .filter(Boolean)
        if (relationshipIds.length > 0) {
          userAnswers.attributes[apiToCamelCaseMap.relationship] =
            relationshipIds.length > 1 ? relationshipIds : relationshipIds[0]
        }
      }
    }

    if (selections.step4.voice) {
      const voiceId = getAttributeId('voice', selections.step4.voice, style, gender)
      if (voiceId) userAnswers.attributes[apiToCamelCaseMap.voice] = voiceId
    }
  }

  return userAnswers
}

// Create model-params from selections
export const createModelParams = async selections => {
  const userAnswers = await convertSelectionsToApiFormat(selections)

  const modelParamsObj = {
    formBody: {
      style: userAnswers.style,
      gender: userAnswers.gender,
      attributes: { ...userAnswers.attributes },
      defaultVoice: null,
    },
  }

  console.log('🚀 Final model params:', JSON.stringify(modelParamsObj, null, 2))

  const json = JSON.stringify(modelParamsObj)
  const base64 = btoa(unescape(encodeURIComponent(json)))

  return base64
}

// Add model params to URL
export const addModelParamsToUrl = (baseUrl, modelParams, additionalParams = {}, navPath = null) => {
  const url = new URL(baseUrl)

  // Swap the destination path (e.g. /paywall → /chats) when the GB nav config
  // asks for it, while keeping every query param — including model-params — intact.
  if (navPath) url.pathname = navPath

  // Add model-params
  url.searchParams.set('model-params', modelParams)

  // Add additional parameters
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value)
    }
  })

  // Add current URL params
  const currentUrlParams = new URLSearchParams(window.location.search)
  for (const [key, value] of currentUrlParams.entries()) {
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value)
    }
  }

  // chat_paywall → /lobby: tag the handoff LAST so it wins over the style type and the
  // landing URL's ?type= forwarded just above. /lobby reads style from model-params.
  if (navPath === resolvePaywallPath('chat_paywall')) {
    url.searchParams.set('type', 'chat_paywall')
  }

  // SFW arm (?variation=sfw) → the platform serves the protected experience.
  // Set after the current-URL spread above so a stale `protection` in the landing
  // query can't shadow the resolved arm. No-op on the NSFW arm.
  applyProtection(url)

  return url.toString()
}

// Everflow attribution for the platform hand-off. Set after any utm spread so the
// id resolved this visit (a replacement click, the cookie) wins over a stale
// ?ef_transaction_id snapshot in utm_data.
const applyEfTransactionId = (url) => {
  const efTransactionId = getEfTransactionId()
  if (efTransactionId) url.searchParams.set('ef_transaction_id', efTransactionId)
}

// duel_quiz arm (123mehfujvu) hand-off — every call site that resolves navType
// to it builds the URL the same way: no character (selected_slug / model-params /
// type / theme / plans / protection are all character-scoped), and only the
// lander's usual attribution rides along (utm/click-ids from utm_data,
// ef_transaction_id, lp_source).
//
// `authType` is NOT hardcoded: the backend's `/duel/quiz` `useLogin` accepts
// `login_pre_lander` only for a LOGIN token (reads the `external_login:<token>`
// Redis key). A REGISTRATION token (password/magic-link sign-up, the Google
// callback for a new user) is written under `external_registration:<token>` —
// `login_paywall` is the auth_type whose backend handler reads BOTH namespaces,
// so every REGISTRATION hand-off must use it. Callers pass the right one; there
// is no default on purpose, so a new call site can't silently inherit the wrong one.
const buildDuelQuizRedirect = (authToken, authType, utm = {}) => {
  const url = new URL(`${SWIPEY_BASE_URL}${resolvePaywallPath('duel_quiz')}`)
  url.searchParams.set('auth_token', authToken)
  url.searchParams.set('auth_type', authType)
  url.searchParams.set('lp_source', LP_SOURCE)
  Object.entries(utm).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value)
  })
  applyEfTransactionId(url)
  return url.toString()
}

// UTM data management - now using our tracking system
export const collectAndStoreUTMData = () => {
  // Initialize tracking if not already done
  if (typeof window !== 'undefined' && window.initTracking) {
    window.initTracking()
  }

  const trackingParams = getTrackingParams()

  // Also get current URL params directly as fallback
  const currentUrlParams = new URLSearchParams(window.location.search)
  const currentParams = {}
  for (const [key, value] of currentUrlParams.entries()) {
    currentParams[key] = value
  }
  const utmObj = {
    ...trackingParams,
    ...currentParams, // Include current URL params as fallback
    path: '/paywall',
  }

  // Store UTM data using our storage utility
  setStorage('utm_data', utmObj)
  return utmObj
}

// Enhanced auth callback handler with model params
export const handleAuthCallback = async (selections = null) => {
  const params = new URLSearchParams(window.location.search)
  const authToken = params.get('auth_token')
  const authType = params.get('auth_type')

  if (authToken && authType) {
    const utm = getStorage('utm_data') || {}
    // GrowthBook isn't initialized yet on the OAuth return, so read the nav
    // config from the value the auth screens persisted before redirecting out.
    const navType = localStorage.getItem('paywallNavType')

    if (isDuelQuiz(navType)) {
      // No character to append. The Google leg can be either a fresh
      // registration or an existing user signing back in — this handler
      // doesn't distinguish them (no `is_new` marker read here) — so
      // auth_type is forced to login_paywall, whose backend handler reads
      // both the external_registration: and external_login: Redis
      // namespaces, regardless of whatever `authType` came back on THIS
      // leg's URL.
      localStorage.removeItem('postSignupRedirect')
      localStorage.removeItem('paywallNavType')
      window.location.replace(buildDuelQuizRedirect(authToken, 'login_paywall', utm))
      return true
    }

    const paywallUrl = new URL(`${SWIPEY_BASE_URL}${resolvePaywallPath(navType)}`)

    if (selections?.welcome) {
      const typeValue = selections.welcome === 'Anime' ? 'anime' : 'realistic'
      paywallUrl.searchParams.set('type', typeValue)
    }

    paywallUrl.searchParams.set('auth_token', authToken)
    paywallUrl.searchParams.set('auth_type', authType)
    paywallUrl.searchParams.set('lp_source', LP_SOURCE)
    // SFW arm (?variation=sfw). GrowthBook isn't initialized yet on the OAuth
    // return, but the variation is resolved from this tab's sticky value, so the
    // marker survives the round-trip. No-op on the NSFW arm.
    applyProtection(paywallUrl)

    // Add type parameter based on selections style
    if (selections?.welcome) {
      const typeValue = selections.welcome === 'Anime' ? 'anime' : 'realistic'
      paywallUrl.searchParams.set('type', typeValue)
    }

    // Add UTM params
    Object.entries(utm).forEach(([key, value]) => {
      if (value) paywallUrl.searchParams.set(key, value)
    })
    applyEfTransactionId(paywallUrl)

    // Add model params if selections are available
    if (selections) {
      try {
        const modelParams = await createModelParams(selections)
        paywallUrl.searchParams.set('model-params', modelParams)
        // Model params added successfully
      } catch (error) {
        // Error adding model params
      }
    }

    // /12 route forces annual; otherwise honor the GB
    // createsexfriends-post-signup-redirect flag persisted at signup time.
    const annualByRoute = isAnnualPlan()
    const annualByFlag = !annualByRoute && localStorage.getItem('postSignupRedirect') === 'annual'
    if (annualByRoute || annualByFlag) {
      paywallUrl.searchParams.set('plans', 'annual')
    }
    // One-shot consumption: clear so next OAuth callback re-derives from
    // current GB value instead of a stale 'annual' from a prior flag state.
    localStorage.removeItem('postSignupRedirect')
    localStorage.removeItem('paywallNavType')

    // chat_paywall → /lobby: tag the handoff LAST so it wins over the style type and the
    // utm spread above (which forwards the landing URL's ?type=). /lobby uses model-params.
    if (navType === 'chat_paywall') paywallUrl.searchParams.set('type', 'chat_paywall')

    window.location.replace(paywallUrl.toString())
    return true // Indicates callback was handled
  }
  return false // No callback to handle
}

// Account creation API with model params
export const createAccount = async (email, password, selections = null) => {
  const ef_transaction_id = getEfTransactionId()
  const payload = { email, password, ef_transaction_id }

  const data = await apiRequest('/auth/account', 'POST', payload)

  if (data.success && data.redirectUrl && selections) {
    try {
      const modelParams = await createModelParams(selections)
      data.redirectUrl = addModelParamsToUrl(data.redirectUrl, modelParams)
      // Model params added successfully
    } catch (err) {
      // Error adding model params
    }
  }

  return data
}

// Paywall-popup flow helper, shared by the password (createPreLanderAccount) and
// magic-link (submitEmailOnly) registration paths so BOTH send the same extras.
//
// For /lobby to drive the popup → Upgate → chat/generation flow, the backend
// needs the popup params in the registration Redis payload. The DTO whitelist
// strips unknown top-level fields, so subscription_required/type/flow are nested
// inside `ab_testing` (typed {[k]:string}, forwarded verbatim into Redis).
// model_params IS a typed top-level DTO field, so it's returned separately.
//
// Returns { abTesting, modelParams } — abTesting always defined; modelParams only
// for the paywall_popup case (undefined otherwise).
// GrowthBook device id for the registration body, popup-first arm ONLY.
//
// That arm is the one where the LP itself resolves a flag whose value the
// backend then charges on (the geo price multiplier), so the two have to bucket
// identically. Every other arm hands the user to the platform paywall, which
// evaluates in its own first-party context — sending the id there would seed
// Lead.gb_device_id (write-once) from the lander and pin the platform to the
// LP's bucket for good.
//
// `country_code` is a FALLBACK only — the backend resolves the country from the
// request IP and that wins; this just covers its lookup coming back empty.
//
// Each key is omitted when unresolved, so a failed lookup leaves the backend on
// its own geo rather than sending an empty string. Returns {} when the arm does
// not apply, so it can be spread into a payload.
const gbBucketingFields = (ab_testing) => {
  if (!isPopupFirst(ab_testing?.['createsexfriend-paywall-order'])) return {}
  const gb_device_id = getGbDeviceId()
  const country_code = getGbCountryCode()
  return {
    ...(gb_device_id ? { gb_device_id } : {}),
    ...(country_code ? { country_code } : {}),
  }
}

const buildPaywallPopupRegistrationExtras = async (ab_testing, selections) => {
  const navType = ab_testing?.['createsexfriend-paywall-nav-config']
  if (navType !== 'paywall_popup') {
    return { abTesting: ab_testing, modelParams: undefined }
  }
  const abTesting = {
    ...ab_testing,
    subscription_required: 'true',
    type: 'paywall_popup',
    flow: ab_testing?.['createsexfriend-paywall-flow'] || 'generation',
  }
  let modelParams
  if (selections) {
    try {
      modelParams = await createModelParams(selections)
    } catch (err) {
      console.error('Error creating model_params for paywall popup', err)
    }
  }
  return { abTesting, modelParams }
}

// Enhanced pre-lander account creation API with model params
export const createPreLanderAccount = async (email, password, selections = null, ab_testing) => {
  const utm = getStorage('utm_data') || {}
  const ef_transaction_id = getEfTransactionId()
  const externalClickIds = await getExternalClickIds()

  const { abTesting: abTestingPayload, modelParams: modelParamsField } =
    await buildPaywallPopupRegistrationExtras(ab_testing, selections)

  const data = await apiRequest('/auth/pre-lander', 'POST', {
    ...utm,
    email,
    password,
    ef_transaction_id,
    ...gbBucketingFields(ab_testing),
    ab_testing: abTestingPayload,
    lp_source: LP_SOURCE,
    externalClickIds,
    ...(modelParamsField ? { model_params: modelParamsField } : {}),
  })

  if (data.message && data.message.includes('User with this email already exists')) {
    return {
      success: false,
      error: 'Email already used',
    }
  }

  // duel_quiz: no character, ever — skip the model-params/theme/plans path below
  // (which runs even with no `selections`, since the backend's own redirectUrl
  // already carries a plain /paywall we'd otherwise leave unswapped).
  const navType = ab_testing?.['createsexfriend-paywall-nav-config']
  const authToken = data.authToken || data.token
  if (data.success && authToken && isDuelQuiz(navType)) {
    // Registration token — external_registration:<token>, not external_login: —
    // login_paywall is the auth_type whose backend handler reads both namespaces.
    data.redirectUrl = buildDuelQuizRedirect(authToken, 'login_paywall', utm)
    return data
  }

  // If successful, add model params to any redirect URL
  if (data.success && data.redirectUrl && selections) {
    const modelParams = await createModelParams(selections)
    const extraParams = {'theme': ab_testing["createsexfriends-paywall-theme"], 'lp_source': LP_SOURCE}
    if (isAnnualPlan()) extraParams['plans'] = 'annual'
    const navPath = resolvePaywallPath(navType)
    data.redirectUrl = addModelParamsToUrl(data.redirectUrl, modelParams, extraParams, navPath)
  }

  // Returns the full backend response, including authToken (registration key) and
  // accessToken (JWT). The popup flow consumes both: authToken in the /lobby
  // baseUrl, accessToken to authorize the checkout call.
  return data
}

// simple_registration AB test (86ca50q0c): create a dummy user WITHOUT collecting
// an email/password on the LP. Mirrors createPreLanderAccount's body (the full
// attribution the backend replays later at checkout) minus email/password.
// Returns { success, authToken, loginUrl }.
export const createTempSimpleRegistration = async (selections = null, ab_testing) => {
  const utm = getStorage('utm_data') || {}
  const ef_transaction_id = getEfTransactionId()
  const externalClickIds = await getExternalClickIds()

  const { abTesting: abTestingPayload, modelParams: modelParamsField } =
    await buildPaywallPopupRegistrationExtras(ab_testing, selections)

  return apiRequest('/auth/pre-lander-temp-simple-registration', 'POST', {
    ...utm,
    ef_transaction_id,
    ...gbBucketingFields(ab_testing),
    ab_testing: abTestingPayload,
    lp_source: LP_SOURCE,
    externalClickIds,
    ...(modelParamsField ? { model_params: modelParamsField } : {}),
  })
}

// Enhanced pre-lander login API with model params
export const loginPreLander = async (email, password, selections = null) => {
  const utm = getStorage('utm_data') || {}
  const ef_transaction_id = getEfTransactionId()

  const payload = {
    ...utm,
    email,
    password,
    ef_transaction_id,
  }

  const data = await apiRequest('/auth/pre-lander', 'POST', payload)

  // Add model params to any redirect URL if selections are available
  if (data.success && data.redirectUrl && selections) {
    const modelParams = await createModelParams(selections)
    data.redirectUrl = addModelParamsToUrl(data.redirectUrl, modelParams, {
      auth_token: data.authToken,
      auth_type: 'user',
    })
    // Enhanced login redirect URL with model params
  }

  return data
}

// Enhanced complete login flow handler with model params
export const handleLoginFlow = async (email, password, selections, ab_testing) => {
  try {
    const utm = getStorage('utm_data') || {}
    const ef_transaction_id = getEfTransactionId()
    const externalClickIds = await getExternalClickIds()

    const data = await apiRequest('/auth/pre-lander/login', 'POST', {
      ...utm,
      email,
      password,
      ef_transaction_id,
      ab_testing,
      lp_source: LP_SOURCE,
      externalClickIds,
    })

    const authToken = data.authToken || data.token
    const user = data

    if (authToken) {
      // Legacy mirror (nothing in this LP reads it back). Expire it with the token
      // it copies — the backend drops external_registration:<token> after an hour,
      // so there's no reason for a spent token to outlive that in localStorage.
      setAuth(authToken, null, LP_SESSION_TTL_MS)

      const isSubscribed = user?.isSubscribed === true
      const isNewAccount = window.location.pathname.includes('/newaccount')
      const isPw = window.location.pathname.includes('/pw')
      const isHalloween = window.location.pathname.includes('/halloween')

      if (isSubscribed) {
        const homeUrl = new URL(`${SWIPEY_BASE_URL}/`)
        homeUrl.searchParams.set('auth_token', authToken)
        homeUrl.searchParams.set('auth_type', 'login_pre_lander')
        homeUrl.searchParams.set('theme', ab_testing['createsexfriends-paywall-theme'])
        homeUrl.searchParams.set('lp_source', LP_SOURCE)
        // SFW arm (?variation=sfw) → the platform serves the protected experience.
        applyProtection(homeUrl)
        Object.entries(utm).forEach(([key, value]) => {
          if (value) homeUrl.searchParams.set(key, value)
        })
        applyEfTransactionId(homeUrl)
        if (isNewAccount) {
          homeUrl.searchParams.set('media', 'paywall-model-02.mp4')
          homeUrl.searchParams.set('pw_v', '1')
        }
        if (isPw) {
          homeUrl.searchParams.set('media', 'vid.mp4')
          homeUrl.searchParams.set('pw_v', '2')
        }
        if (isHalloween) {
          homeUrl.searchParams.set('media', 'vid.mp4')
        }
        console.log('homeUrl', homeUrl)
        return {
          success: true,
          redirectUrl: homeUrl.toString(),
        }
      } else {
        const navType = ab_testing?.['createsexfriend-paywall-nav-config']

        // duel_quiz: keeps today's subscriber destination above when isSubscribed
        // is true; a NON-subscribed login in this arm still goes to the quiz, no
        // character attached.
        if (isDuelQuiz(navType)) {
          // A genuine login token (POST /auth/pre-lander/login) — lives under
          // external_login:<token>, which login_pre_lander's handler reads;
          // already correct, unlike the registration-token sites elsewhere in
          // this file.
          return {
            success: true,
            redirectUrl: buildDuelQuizRedirect(authToken, 'login_pre_lander', utm),
          }
        }

        const paywallUrl = new URL(`${SWIPEY_BASE_URL}${resolvePaywallPath(navType)}`)
        paywallUrl.searchParams.set('auth_token', authToken)
        paywallUrl.searchParams.set('auth_type', 'login_paywall')
        paywallUrl.searchParams.set('theme', ab_testing['createsexfriends-paywall-theme'])
        paywallUrl.searchParams.set('lp_source', LP_SOURCE)
        // SFW arm (?variation=sfw) → the platform serves the protected experience.
        applyProtection(paywallUrl)

        if (selections?.welcome) {
          const typeValue = selections.welcome === 'Anime' ? 'anime' : 'realistic'
          paywallUrl.searchParams.set('type', typeValue)
        }

        Object.entries(utm).forEach(([key, value]) => {
          if (value) paywallUrl.searchParams.set(key, value)
        })
        applyEfTransactionId(paywallUrl)

        if (selections) {
          try {
            const modelParams = await createModelParams(selections)
            paywallUrl.searchParams.set('model-params', modelParams)
          } catch (err) {
            console.error('Error adding model-params', err)
          }
        }

        if (isNewAccount) {
          paywallUrl.searchParams.set('media', 'paywall-model-02.mp4')
          paywallUrl.searchParams.set('pw_v', '1')
        }
        if (isPw) {
          paywallUrl.searchParams.set('media', 'vid.mp4')
          paywallUrl.searchParams.set('pw_v', '2')
        }
        if (isHalloween) {
          paywallUrl.searchParams.set('media', 'vid.mp4')
        }
        if (isAnnualPlan()) {
          paywallUrl.searchParams.set('plans', 'annual')
        }
        // chat_paywall → /lobby: tag the handoff LAST so it wins over the style type and the
        // utm spread above (which forwards the landing URL's ?type=). /lobby uses model-params.
        if (navType === 'chat_paywall') paywallUrl.searchParams.set('type', 'chat_paywall')
        return {
          success: true,
          redirectUrl: paywallUrl.toString(),
        }
      }
    }

    return {
      success: false,
      message: 'Please check your email or password',
    }
  } catch (error) {
    return {
      success: false,
      message: 'Please check your email or password',
    }
  }
}

// ---- Magic-link / email-only OTP flow (createsexfriends-magicLink = ON) ----

// Append model-params, theme, plans, and media/pw_v params (the same set the
// password flows build) onto the loginUrl the backend returned.
//
// Both callers share this function: submitEmailOnly (a NEW email — always a
// fresh, non-subscribed registration) and verifyPreLanderOtp (an EXISTING
// email's OTP login, which may already be subscribed). An already-subscribed
// visitor keeps today's destination — see the duel_quiz guard below.
const decorateEmailOnlyRedirect = async (loginUrl, selections, ab_testing, isSubscribed = false) => {
  if (!loginUrl) return null

  const url = new URL(loginUrl)

  // Redirect to /chats (or /duel/quiz) instead of /paywall when the nav config
  // asks for it; model-params is appended below either way. A subscribed
  // visitor is treated as the default 'paywall' arm here — same as every other
  // arm — so an OTP login by an existing subscriber goes where a subscriber in
  // the 'paywall' arm goes today: the backend's loginUrl rewritten to /paywall,
  // with its own auth_type kept, instead of the quiz.
  const rawNavType = ab_testing?.['createsexfriend-paywall-nav-config']
  const navType = isDuelQuiz(rawNavType) && isSubscribed ? undefined : rawNavType
  url.pathname = resolvePaywallPath(navType)

  const duelQuiz = isDuelQuiz(navType)
  if (duelQuiz) {
    // Both a fresh registration (submitEmailOnly) and an OTP login
    // (verifyPreLanderOtp) land here — login_paywall's backend handler reads
    // both the external_registration: and external_login: Redis namespaces, so
    // it is correct for either token type without having to tell them apart.
    url.searchParams.set('auth_type', 'login_paywall')
    // The other duel_quiz redirect sites all set lp_source (buildDuelQuizRedirect);
    // this one starts from the backend's own loginUrl instead, which doesn't
    // carry it — the platform reads the arriving lp_source as entry_lp_source.
    url.searchParams.set('lp_source', LP_SOURCE)
  } else {
    // SFW arm (?variation=sfw) → the platform serves the protected experience.
    applyProtection(url)

    if (selections?.welcome) {
      url.searchParams.set('type', selections.welcome === 'Anime' ? 'anime' : 'realistic')
    }

    if (selections) {
      try {
        const modelParams = await createModelParams(selections)
        url.searchParams.set('model-params', modelParams)
      } catch (err) {
        console.error('Error adding model-params to magic-link redirect', err)
      }
    }

    const theme = ab_testing?.['createsexfriends-paywall-theme']
    if (theme) url.searchParams.set('theme', theme)

    const path = window.location.pathname
    if (path.includes('/newaccount')) {
      url.searchParams.set('media', 'paywall-model-02.mp4')
      url.searchParams.set('pw_v', '1')
    }
    if (path.includes('/pw')) {
      url.searchParams.set('media', 'vid.mp4')
      url.searchParams.set('pw_v', '2')
    }
    if (path.includes('/halloween')) {
      url.searchParams.set('media', 'vid.mp4')
    }

    // /12 forces annual; otherwise honor GB redirect flag persisted at signup.
    const annualByRoute = isAnnualPlan()
    const annualByFlag = !annualByRoute && localStorage.getItem('postSignupRedirect') === 'annual'
    if (annualByRoute || annualByFlag) {
      url.searchParams.set('plans', 'annual')
    }
  }

  // Forward the landing tracking params (twclid, gclid, utm_*, etc.) onto the paywall
  // redirect so magic-link matches the password flow. We read the stored utm_data
  // snapshot (the same source handleLoginFlow uses) — NOT window.location.search,
  // which SPA navigate({replace:true}) strips before submit.
  const utm = getStorage('utm_data') || {}
  Object.entries(utm).forEach(([key, value]) => {
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value)
    }
  })
  applyEfTransactionId(url)
  // Click-ids (twclid, gclid, fbclid, ttclid) come from the landing-URL snapshot the
  // codebase trusts for them (utm_data is not a reliable source for click-ids).
  const forwardClickIds = await getExternalClickIds()
  Object.entries(forwardClickIds).forEach(([key, value]) => {
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value)
    }
  })

  // chat_paywall → /lobby: tag the handoff LAST so it wins over the style type and the
  // utm/landing ?type= forwarded above. /lobby reads style from model-params.
  if (navType === 'chat_paywall') url.searchParams.set('type', 'chat_paywall')

  return url.toString()
}

// POST /auth/pre-lander-with-email-only — used by both signup and login
// when the magic-link flag is ON. Returns either:
//   - otp_sent: true + otpExpiry  (existing email → caller shows OTP modal)
//   - authToken + redirectUrl     (new email → caller navigates directly)
export const submitEmailOnly = async (email, { isResend = false, selections, ab_testing } = {}) => {
  const utm = getStorage('utm_data') || {}
  const ef_transaction_id = getEfTransactionId()
  const externalClickIds = await getExternalClickIds()

  // Same paywall-popup extras as the password flow: for a NEW email this endpoint
  // registers the user via the same path, so /lobby needs model_params +
  // subscription_required/type/flow in the Redis payload to drive the popup flow.
  const { abTesting: abTestingPayload, modelParams: modelParamsField } =
    await buildPaywallPopupRegistrationExtras(ab_testing, selections)

  const payload = {
    ...utm,
    email,
    ef_transaction_id,
    ...gbBucketingFields(ab_testing),
    ab_testing: abTestingPayload,
    isResend,
    lp_source: LP_SOURCE,
    externalClickIds,
    ...(modelParamsField ? { model_params: modelParamsField } : {}),
  }

  const data = await apiRequest('/auth/pre-lander-with-email-only', 'POST', payload)

  if (data?.success === false) return data

  if (data?.authToken && data?.loginUrl) {
    const redirectUrl = await decorateEmailOnlyRedirect(
      data.loginUrl,
      selections,
      ab_testing,
      data?.isSubscribed === true,
    )
    if (redirectUrl) data.redirectUrl = redirectUrl
    if (data.authToken) setAuth(data.authToken, null, LP_SESSION_TTL_MS) // legacy mirror, see above
  }

  return data
}

// POST /auth/pre-lander/login-with-otp — verifies the OTP and returns the
// authToken + loginUrl (same shape as the auto-login branch of submitEmailOnly).
export const verifyPreLanderOtp = async (email, otp, { selections, ab_testing } = {}) => {
  const data = await apiRequest('/auth/pre-lander/login-with-otp', 'POST', { email, otp })

  if (data?.success === false) return data

  const authToken = data?.authToken || data?.token
  const loginUrl = data?.loginUrl

  if (authToken) setAuth(authToken, null, LP_SESSION_TTL_MS) // legacy mirror, see above

  if (loginUrl) {
    const redirectUrl = await decorateEmailOnlyRedirect(
      loginUrl,
      selections,
      ab_testing,
      data?.isSubscribed === true,
    )
    if (redirectUrl) data.redirectUrl = redirectUrl
  }

  return data
}

export const getGoogleAuthUrl = () => `${SWIPEY_BASE_URL}/api/v1/auth/google/login`

// Swipey base URL
export const getSwipeyBaseUrl = () => SWIPEY_BASE_URL

// Export API configuration for debugging
export const getApiConfig = () => ({
  baseUrl: API_BASE_URL,
  swipeyBaseUrl: SWIPEY_BASE_URL,
  googleAuthUrl: getGoogleAuthUrl(),
  hasApiKey: !!API_KEY && API_KEY !== 'your-api-key-here',
  attributesLoaded,
  attributesMap,
})

// ---- Add to your API service file ----

// Request password reset (send code)
export const requestPasswordReset = async email => {
  return apiRequest('/auth/reset-password', 'POST', { email })
}

// Verify OTP for password reset
export const verifyPasswordOtp = async (email, otp) => {
  return apiRequest('/auth/verify-reset-password-otp', 'POST', { email, otp })
}

// Set new password using token
export const updatePassword = async (token, newPassword) => {
  return apiRequest(`/auth/set-new-password?token=${token}`, 'POST', {
    newPassword,
  })
}
